import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import {
  type SuperAdminDebugOverlayReadModelRequest,
  type SuperAdminDebugOverlayReadModelResponse,
  invokeSuperAdminDebugOverlayReadModel,
} from '@/lib/readModels';

interface UseSuperAdminDebugOverlayOptions extends SuperAdminDebugOverlayReadModelRequest {
  enabled: boolean;
}

const REFRESH_INTERVAL_MS = 15_000;

export function useSuperAdminDebugOverlay(options: UseSuperAdminDebugOverlayOptions) {
  const queryClient = useQueryClient();
  const queryKey = ['super-admin-debug-overlay', 'global', options.surface, options.pathname, options.enabledAt];

  const query = useQuery<SuperAdminDebugOverlayReadModelResponse>({
    queryKey,
    queryFn: () => invokeSuperAdminDebugOverlayReadModel({
      organizationId: options.organizationId ?? null,
      surface: options.surface,
      pathname: options.pathname,
      enabledAt: options.enabledAt,
    }),
    enabled: options.enabled,
    staleTime: 0,
    refetchInterval: options.enabled ? REFRESH_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!options.enabled) {
      return;
    }

    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey });
    };

    const channels = [
      supabase
        .channel('super-admin-debug-overlay:audit:global')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'audit_events',
          },
          invalidate,
        )
        .subscribe(),
      supabase
        .channel('super-admin-debug-overlay:deliveries:global')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notification_deliveries',
          },
          invalidate,
        )
        .subscribe(),
      supabase
        .channel('super-admin-debug-overlay:budgets')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'third_party_api_budget_windows',
          },
          invalidate,
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'ai_agent_config',
          },
          invalidate,
        )
        .subscribe(),
    ];

    return () => {
      channels.forEach((channel) => {
        void supabase.removeChannel(channel);
      });
    };
  }, [options.enabled, queryClient, queryKey]);

  return query;
}
